import { useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import useColumnStateManager from "../../hooks/useColumnStateManager";

/**
 * Advanced AgGrid component with zero-flash column state persistence
 */
const AgGridWithAdvancedColumnState = ({
  tableId,
  columnDefs: originalColumnDefs,
  onGridReady: customOnGridReady,
  onFirstDataRendered: customOnFirstDataRendered,
  defaultColDef = {},
  enableColumnState = true,
  gridApiRef: externalGridApiRef,
  style = {},
  ...agGridProps
}) => {
  //   console.log("🔧 AgGridWithAdvancedColumnState rendering with:", { tableId, enableColumnState });

  // Use advanced column state manager for pre-configuration
  const {
    columnDefs: configuredColumnDefs,
    isLoading: isColumnConfigLoading,
    saveColumnState: saveColumnStateManager,
    hasSavedState,
  } = useColumnStateManager(enableColumnState ? tableId : null, originalColumnDefs);

  // Create our own gridApiRef for managing the grid API
  const gridApiRef = useRef(null);

  // Enhanced default column definitions
  const enhancedDefaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 50,
    lockPosition: false,
    ...defaultColDef,
  };

  // Combined grid ready handler
  const handleOnGridReady = useCallback(
    (params) => {
      // Set our internal gridApiRef
      gridApiRef.current = params.api;

      // Set external ref if provided
      if (externalGridApiRef) {
        externalGridApiRef.current = params.api;
      }

      //   console.log(`🚀 Advanced grid ready for ${tableId}`, !!params.api);

      // Call custom onGridReady if provided
      if (customOnGridReady) {
        customOnGridReady(params);
      }
    },
    [externalGridApiRef, customOnGridReady, tableId]
  );

  // Combined first data rendered handler
  const handleOnFirstDataRendered = useCallback(
    (params) => {
      //   console.log(`📊 Advanced grid first data rendered for ${tableId}`);

      // Call custom onFirstDataRendered if provided
      if (customOnFirstDataRendered) {
        customOnFirstDataRendered(params);
      }
    },
    [customOnFirstDataRendered, tableId]
  );

  // Column event handler with advanced saving
  const handleColumnEvent = useCallback(() => {
    if (gridApiRef.current && enableColumnState) {
      //   console.log(`🔄 Advanced column event triggered for ${tableId}`);
      // Debounce the save operation
      setTimeout(() => {
        try {
          const columnState = gridApiRef.current.getColumnState();
          saveColumnStateManager(columnState);
        } catch (error) {
          console.error(`❌ Failed to get column state for ${tableId}:`, error);
        }
      }, 300);
    }
  }, [enableColumnState, tableId, saveColumnStateManager]);

  // Column event handlers (only if column state is enabled)
  const columnEventHandlers = enableColumnState
    ? {
        onColumnMoved: handleColumnEvent,
        onColumnResized: handleColumnEvent,
        onColumnVisible: handleColumnEvent,
        onColumnPinned: handleColumnEvent,
      }
    : {};

  // Show loading state while column configuration is loading
  if (isColumnConfigLoading) {
    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
        }}>
        <div>Loading table configuration...</div>
      </div>
    );
  }

  return (
    <div style={style}>
      <AgGridReact
        {...agGridProps}
        ref={gridApiRef}
        columnDefs={configuredColumnDefs}
        defaultColDef={enhancedDefaultColDef}
        onGridReady={handleOnGridReady}
        onFirstDataRendered={handleOnFirstDataRendered}
        {...columnEventHandlers}
        onSortChanged={enableColumnState ? handleColumnEvent : undefined}
        onFilterChanged={enableColumnState ? handleColumnEvent : undefined}
        // Suppress column animations to prevent flash
        suppressColumnMoveAnimation={true}
        suppressAnimationFrame={true}
      />
    </div>
  );
};

export default AgGridWithAdvancedColumnState;
